import { useEffect, useState } from "react";
import { Alert } from "@/components/ui/alert";
import { listAuditEvents, type AuditEvent } from "@/shared/api/audit";

/** History timeline — plan/23; embedded for DocActivity — plan/35 */
export function HistoryPanel(props: {
  companySlug: string;
  docType: string;
  docId: string;
  embedded?: boolean;
}) {
  const { companySlug, docType, docId, embedded } = props;
  const [open, setOpen] = useState(Boolean(embedded));
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open || loaded) return;
    let cancelled = false;
    listAuditEvents(companySlug, { docType, docId, limit: 50 })
      .then((d) => {
        if (cancelled) return;
        setEvents(d.events);
        setLoaded(true);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, loaded, companySlug, docType, docId]);

  const timeline = (
    <div className={embedded ? undefined : "mt-3 animate-[fadeIn_220ms_ease-out]"}>
      {error && (
        <Alert variant="destructive" className="mb-3">
          {error}
        </Alert>
      )}
      <ol className="m-0 list-none space-y-3 border-l border-[var(--color-border)] pl-4 p-0">
        {events.map((e) => (
          <li key={e.id} className="relative text-sm">
            <span className="absolute -left-[1.15rem] top-1.5 h-2 w-2 rounded-full bg-[var(--color-border)]" />
            <div className="font-medium">{e.label}</div>
            <div className="text-xs text-[var(--color-muted)]">
              {e.actorLabel}
              {e.summary ? ` · ${e.summary}` : ""}
              {" · "}
              {new Date(e.createdAt).toLocaleString("en-PH", {
                timeZone: "Asia/Manila",
              })}
            </div>
          </li>
        ))}
        {loaded && events.length === 0 && !error && (
          <li className="text-sm text-[var(--color-muted)]">No history yet</li>
        )}
        {!loaded && !error && (
          <li>
            <Alert variant="muted" className="py-2 text-xs">
              Loading history…
            </Alert>
          </li>
        )}
      </ol>
    </div>
  );

  if (embedded) return timeline;

  return (
    <section className="mt-8 border-t border-[var(--color-border)] pt-4">
      <button
        type="button"
        className="text-sm font-medium text-[var(--color-muted)] hover:text-[var(--color-foreground)]"
        onClick={() => setOpen((v) => !v)}
      >
        History {open ? "▴" : "▾"}
      </button>
      {open ? timeline : null}
    </section>
  );
}
